// src/screens/HostProgramScreen.js
// POST-PIVOT host reward (2026-07-16): hosts earn MINTED Crits per round, scaled
// to the lobby pot they ran — 20% of (players in the match × entry), floor 1 /
// cap 25 per round, with a depth multiplier that grows the more rounds a host
// runs in the same event (1st ×1.0 … 5th+ ×2.0). Not cash, not redeemable. The
// old cash-tier model (escrow earnings, 10-tier multipliers, Host Points, $
// cash-out) is kept in code but its UI is gated behind HOST_CASH_TIERS=false.
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView,
  TouchableOpacity, ActivityIndicator, Switch, Modal, Pressable
} from 'react-native';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebaseConfig';

// Icon imports - using @expo/vector-icons
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import { getHostTier, formatHostReward, HOST_REWARD_CONFIG, HOST_CRIT_REWARD_CONFIG, computeHostRoundReward } from '../constants/hostRewards';
import { fmtCrits } from '../utils/currencyHelper';
import useCritsEnabled from '../hooks/useCritsEnabled';
import { openTermsOfService } from '../utils/openTermsOfService';

// Active host-reward model = % of the match lobby pot, minted as Crits.
const HOST_POT_PCT_LABEL = `${Math.round(HOST_CRIT_REWARD_CONFIG.potPct * 100)}%`;

// A concrete worked example for the "How It Works" section — computed live from
// the reward config so it stays correct if the levers are ever retuned.
// Sample: a GT7 race, 15 racers, 3 Crits to enter.
const EX_RACERS = 15;
const EX_ENTRY = 3;
const EX_POT = EX_RACERS * EX_ENTRY;
const EX_ROUNDS = [1, 2, 3].map((ordinal) =>
  computeHostRoundReward({ entryCrits: EX_ENTRY, playersInMatch: EX_RACERS, ordinal })
);
const EX_TOTAL = EX_ROUNDS.reduce((sum, r) => sum + r.crits, 0);

// Active host reward = % of the lobby pot in Crits (see HOST_CRIT_REWARD_CONFIG).
// HOST_CASH_TIERS=false hides the dormant cash-tier UI (escrow earnings, 10-tier
// ladder, Host Points, $ cash-out). Flip to true only if cash hosting is revived.
const HOST_CASH_TIERS = false;

// ============================================================================
// ICON HELPER - Maps icon names to components
// ============================================================================
const getIcon = (name, size = 24, color = '#FCD000') => {
  const icons = {
    'warning': <Ionicons name="warning" size={size} color={color} />,
    'wallet': <Ionicons name="wallet" size={size} color={color} />,
    'target': <MaterialCommunityIcons name="target" size={size} color={color} />,
    'star': <FontAwesome5 name="star" size={size} color={color} />,
    'credit-card': <Ionicons name="card" size={size} color={color} />,
    'cash': <Ionicons name="cash" size={size} color={color} />,
    'trophy': <Ionicons name="trophy" size={size} color={color} />,
    'trending-up': <Ionicons name="trending-up" size={size} color={color} />,
    'checkmark': <Ionicons name="checkmark-circle" size={size} color={color} />,
    'time': <Ionicons name="time" size={size} color={color} />,
    'people': <Ionicons name="people" size={size} color={color} />,
    'game-controller': <Ionicons name="game-controller" size={size} color={color} />,
    'settings': <Ionicons name="settings" size={size} color={color} />,
    'gift': <Ionicons name="gift" size={size} color={color} />,
  };
  return icons[name] || <Ionicons name="ellipse" size={size} color={color} />;
};

export default function HostProgramScreen({ navigation }) {
  const { showError } = useError();
  const [user, setUser] = useState(null);
  const [hostData, setHostData] = useState(null);
  const [escrowData, setEscrowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [showHostingAgreement, setShowHostingAgreement] = useState(false);
  // Economy flag (evergreen step 2.3): with Crits off, the reward copy on this
  // page goes money-free (hosted rounds go on the host's record; no copy
  // ties hosting to pay, per copy doctrine v3). The reward
  // config mirror in ../constants/hostRewards is untouched.
  const critsEnabled = useCritsEnabled();

  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged(u => {
      setUser(u);
      if (!u) {
        setLoading(false);
        navigation.replace('Login');
      }
    });

    return unsubAuth;
  }, []);

  useEffect(() => {
    if (!user) return;

    // Listen to host data
    const unsubHost = onSnapshot(
      doc(db, 'hosts', user.uid),
      (snap) => {
        if (snap.exists()) {
          setHostData(snap.data());
        } else {
          setHostData({
            isHostOptIn: false,
            hostTier: 1,
            hostPoints: 0,
            hostStats: {
              totalMatchesHosted: 0,
              eventsCompletedAsHost: 0,
              successfulHostings: 0,
              failedHostings: 0,
              activeHostings: 0,
            },
          });
        }
      },
      (error) => {
        console.error('Error loading host data:', error);
        showError('Error', 'Failed to load host data');
      }
    );

    // Listen to escrow data (accumulated earnings)
    const unsubEscrow = onSnapshot(
      doc(db, 'hostEscrow', user.uid),
      (snap) => {
        if (snap.exists()) {
          setEscrowData(snap.data());
        } else {
          setEscrowData({
            pendingCents: 0,
            totalEarnedCents: 0,
            claimedCents: 0,
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading escrow data:', error);
        setEscrowData({
          pendingCents: 0,
          totalEarnedCents: 0,
          claimedCents: 0,
        });
        setLoading(false);
      }
    );

    return () => {
      unsubHost();
      unsubEscrow();
    };
  }, [user]);

  const handleOptInToggle = async (value) => {
    if (value) {
      setShowHostingAgreement(true);
      return;
    }
    await processOptInChange(false);
  };

  const processOptInChange = async (value) => {
    setUpdating(true);
    try {
      const functions = getFunctions();
      const updateOptIn = httpsCallable(functions, 'updateHostOptIn');
      await updateOptIn({ optIn: value });

      showError('Success', value
        ? (critsEnabled
          ? `You are now opted into the Host Program! Sign up to host events and earn Crits every round you host — ${HOST_POT_PCT_LABEL} of the lobby's Crit pool, and more the more rounds you run.`
          : 'You are now opted into the Host Program. Sign up to host events that need a host — hosting is free, always your choice, and every round you run goes on your record.')
        : 'You have opted out of the Host Program. You won\'t be asked to host any events — hosting is always your choice.'
      );
    } catch (error) {
      console.error('Error updating opt-in:', error);
      showError('Error', 'Failed to update hosting preference');
    } finally {
      setUpdating(false);
    }
  };

  const handleAcceptHostingAgreement = async () => {
    setShowHostingAgreement(false);
    await processOptInChange(true);
  };

  const handleDeclineHostingAgreement = () => {
    setShowHostingAgreement(false);
  };

  const handleClaimEarnings = async () => {
    const pendingCents = escrowData?.pendingCents || 0;
    
    // Minimum $10.00 to claim (1000 cents)
    const minCashOutCents = 1000;
    
    if (pendingCents < minCashOutCents) {
      const needed = minCashOutCents - pendingCents;
      showError(
        'Minimum Not Met', 
        `You need ${formatHostReward(needed / 100)} more to reach the $10.00 minimum cash-out threshold.`
      );
      return;
    }

    setClaiming(true);
    try {
      const functions = getFunctions();
      const claimRewards = httpsCallable(functions, 'claimHostEarnings');
      const result = await claimRewards();

      showError('Success!', `${formatHostReward(result.data.claimedCents / 100)} has been released to your wallet!`);
    } catch (error) {
      console.error('Error claiming rewards:', error);
      showError('Error', error.message || 'Failed to claim earnings');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={styles.loadingText}>Loading Host Program...</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  if (!user) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.centerContainer}>
          <Text style={styles.errorText}>Please log in to access the Host Program</Text>
          <TouchableOpacity 
            style={styles.button}
            onPress={() => navigation.navigate('Login')}
          >
            <Text style={styles.buttonText}>Go to Login</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  const hostPoints = hostData?.hostPoints || 0;
  const tier = getHostTier(hostPoints);
  const pendingCents = escrowData?.pendingCents || 0;
  const totalEarnedCents = escrowData?.totalEarnedCents || 0;
  const claimedCents = escrowData?.claimedCents || 0;
  const stats = hostData?.hostStats || {};
  const successRate = stats.totalMatchesHosted > 0
    ? ((stats.successfulHostings / stats.totalMatchesHosted) * 100).toFixed(1)
    : '0.0';

  // Rating data (Phase 5)
  const averageRating = hostData?.averageRating || 0;
  const totalRatings = hostData?.totalRatings || 0;
  const ratingBreakdown = hostData?.ratingBreakdown || {};
  const displayRating = totalRatings > 0 ? (averageRating / 2).toFixed(1) : '--'; // Convert 1-10 to 1-5 stars

  // Minimum $10.00 to claim (1000 cents)
  const canClaim = pendingCents >= 1000;

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      
      {/* Hosting Agreement Modal */}
      <Modal
        visible={showHostingAgreement}
        transparent={true}
        animationType="fade"
        onRequestClose={handleDeclineHostingAgreement}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainer}>
            <ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent}>
              <View style={styles.modalHeader}>
                <View style={styles.modalIconContainer}>
                  {getIcon('warning', 48, '#f39c12')}
                </View>
                <Text style={styles.modalTitle}>Host Program Agreement</Text>
              </View>

              <View style={styles.modalContent}>
                <Text style={styles.modalSectionTitle}>Host Responsibilities</Text>
                <Text style={styles.modalText}>
                  As a host, you agree to:
                </Text>
                
                <View style={styles.modalListItem}>
                  <Text style={styles.modalBullet}>-</Text>
                  <Text style={styles.modalListText}>
                    Create game lobbies for each match
                  </Text>
                </View>
                
                <View style={styles.modalListItem}>
                  <Text style={styles.modalBullet}>-</Text>
                  <Text style={styles.modalListText}>
                    Invite the correct players to each lobby
                  </Text>
                </View>
                
                <View style={styles.modalListItem}>
                  <Text style={styles.modalBullet}>-</Text>
                  <Text style={styles.modalListText}>
                    Set game parameters per event rules
                  </Text>
                </View>
                
                <View style={styles.modalListItem}>
                  <Text style={styles.modalBullet}>-</Text>
                  <Text style={styles.modalListText}>
                    Start the match when it's time to play
                  </Text>
                </View>
                
                <View style={styles.modalListItem}>
                  <Text style={styles.modalBullet}>-</Text>
                  <Text style={styles.modalListText}>
                    Maintain fair and unbiased hosting
                  </Text>
                </View>

                <View style={styles.modalNote}>
                  <Ionicons name="information-circle" size={18} color="#3498db" />
                  <Text style={styles.modalNoteText}>
                    You don't need to referee or review evidence - our vision system handles that automatically. You just make sure games run on time!
                  </Text>
                </View>

                {critsEnabled ? (
                  <>
                    <Text style={styles.modalSectionTitle}>How You're Rewarded</Text>
                    <Text style={styles.modalText}>
                      <Text style={styles.modalBold}>You earn only on events you signed up to host - hosts are never auto-assigned.</Text>{'\n\n'}
                      <Text style={styles.modalBold}>Crits scaled to the lobby you run:</Text>{'\n'}
                      - Each round you host pays {HOST_POT_PCT_LABEL} of that lobby's Crit pool (players in the match x entry), minted as Crits{'\n'}
                      - Bigger lobbies pay more — a full 15-racer grid pays far more than a 1v1{'\n'}
                      - Every hosted round pays at least 1 Crit, so you always earn something{'\n\n'}

                      <Text style={styles.modalBold}>The more rounds you host, the more each pays:</Text>{'\n'}
                      - Host multiple rounds of the same event and each successive round earns a bigger multiplier{'\n'}
                      - Reward climbs from 1x up to 2x as you carry more of the event{'\n\n'}

                      <Text style={styles.modalBold}>Good to Know:</Text>{'\n'}
                      - Crits are not cash and aren't redeemable for cash{'\n'}
                      - Spend the Crits you earn to enter more events{'\n'}
                      - One reward per round — you can't earn twice in the same round
                    </Text>
                  </>
                ) : (
                  <>
                    <Text style={styles.modalSectionTitle}>How Hosting Works</Text>
                    <Text style={styles.modalText}>
                      <Text style={styles.modalBold}>You host only events you signed up for - hosts are never auto-assigned.</Text>{'\n\n'}
                      - Hosting is free — no entry to host{'\n'}
                      - Every round you host goes on your record{'\n'}
                      - Keep hosting rounds of the same event and you carry more of it
                    </Text>
                  </>
                )}

                <Text style={styles.modalSectionTitle}>Important Notes</Text>
                <Text style={styles.modalText}>
                  - You're never auto-assigned a host role - you sign up{'\n'}
                  {critsEnabled
                    ? '- 1v1 and ordinary solo events have no host reward (players run their own lobby)'
                    : '- 1v1 and ordinary solo events have no host (players run their own lobby)'}{'\n'}
                  {critsEnabled
                    ? '- Crits are credited to your balance automatically when the event completes'
                    : '- Hosted rounds are recorded when the event completes'}
                </Text>

                <Text style={styles.modalSectionTitle}>Failure Penalties</Text>
                <Text style={styles.modalText}>
                  Hosting is a responsibility. Failing to show up or complete your duties has consequences:
                </Text>
                
                <View style={styles.modalPenaltyList}>
                  <View style={styles.modalPenaltyRow}>
                    <Text style={[styles.modalPenaltyBadge, { color: '#2ecc71' }]}>1st</Text>
                    <Text style={styles.modalPenaltyText}>Warning only</Text>
                  </View>
                  <View style={styles.modalPenaltyRow}>
                    <Text style={[styles.modalPenaltyBadge, { color: '#f39c12' }]}>2nd</Text>
                    <Text style={styles.modalPenaltyText}>Strike on your hosting record</Text>
                  </View>
                  <View style={styles.modalPenaltyRow}>
                    <Text style={[styles.modalPenaltyBadge, { color: '#e74c3c' }]}>3rd</Text>
                    <Text style={styles.modalPenaltyText}>Final Warning + hosting suspended</Text>
                  </View>
                  <View style={styles.modalPenaltyRow}>
                    <Text style={[styles.modalPenaltyBadge, { color: '#c0392b' }]}>4th</Text>
                    <Text style={styles.modalPenaltyText}>Account Ban</Text>
                  </View>
                </View>

                <View style={styles.modalWarningBox}>
                  <Ionicons name="alert-circle" size={16} color="#e74c3c" />
                  <Text style={styles.modalWarningText}>
                    Failures are tracked within a 30-day rolling window. 8+ lifetime failures also results in a ban.
                  </Text>
                </View>

                <View style={styles.modalDisclaimer}>
                  <Text style={styles.modalDisclaimerText}>
                    By accepting, you acknowledge that you have read, understood, and agree to these terms as part of the CritCash Host Program.
                  </Text>
                  {/* Terms Section 5 is the Host Terms accepted on opt-in. Web opens
                      a new tab (this modal stays up); native closes the modal first
                      so it doesn't cover the Terms screen. */}
                  <TouchableOpacity
                    onPress={() => openTermsOfService(navigation, {
                      onBeforeNavigate: () => setShowHostingAgreement(false),
                    })}
                  >
                    <Text style={[styles.modalDisclaimerText, styles.modalLink]}>
                      Host Terms (Section 5 of the Terms of Service)
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>

            <View style={styles.modalActions}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.modalButtonDecline]}
                onPress={handleDeclineHostingAgreement}
              >
                <Text style={styles.modalButtonTextDecline}>Decline</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.modalButton, styles.modalButtonAccept]}
                onPress={handleAcceptHostingAgreement}
              >
                <Text style={styles.modalButtonTextAccept}>Accept & Opt In</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      
      <ScrollView style={styles.scrollView}>
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <View style={[styles.tierBadgeLarge, { backgroundColor: '#FCD00020', borderColor: '#FCD000' }]}>
            <FontAwesome5 name="star" size={36} color="#FCD000" />
          </View>
          <Text style={styles.heroTitle}>Host Program</Text>
          <Text style={styles.heroSubtitle}>{critsEnabled
            ? 'Sign up to host events and earn Crits for every round you run. Bigger lobbies pay more — and the longer you stay on to host, the more each round pays.'
            : 'Sign up to host events that need a host. Hosting is free, always your choice, and every round you run goes on your record.'}</Text>
          {HOST_CASH_TIERS && (
            <>
              <View style={styles.tierBadge}>
                <Text style={[styles.tierBadgeText, { color: tier.color }]}>{tier.name}</Text>
              </View>
              <Text style={styles.hostPointsText}>{hostPoints} Host Points</Text>
              {tier.nextTier && (
                <Text style={styles.progressText}>
                  {tier.pointsToNext} points to {tier.nextTier}
                </Text>
              )}
            </>
          )}
        </View>

        {/* Opt-In Toggle */}
        <View style={styles.section}>
          <View style={styles.optInCard}>
            <View style={styles.optInLeft}>
              <Text style={styles.optInTitle}>Hosting Status</Text>
              <Text style={styles.optInDescription}>
                {hostData?.isHostOptIn
                  ? (critsEnabled
                    ? `You're opted in. Sign up to host events that need a host and earn ${HOST_POT_PCT_LABEL} of each lobby's Crit pool — no entry fee to host.`
                    : 'You\'re opted in. Sign up to host events that need a host — no entry fee to host.')
                  : (critsEnabled
                    ? `Opt in, then sign up to host events that need a host. Only hosts who claim a spot earn the reward — ${HOST_POT_PCT_LABEL} of the lobby's Crit pool per round, no entry fee.`
                    : 'Opt in, then sign up to host events that need a host. You host only the events you claim — no entry fee.')
                }
              </Text>
            </View>
            <Switch
              value={hostData?.isHostOptIn}
              onValueChange={handleOptInToggle}
              disabled={updating}
              trackColor={{ false: '#333', true: '#00A86B' }}
              thumbColor={hostData?.isHostOptIn ? '#FCD000' : '#888'}
            />
          </View>
        </View>

        {/* Rewards Section — with Crits off there is no reward figure to show,
            so the whole card goes (Your Statistics below still carries the
            hosting record). */}
        {(HOST_CASH_TIERS || critsEnabled) ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your Rewards</Text>

          {HOST_CASH_TIERS ? (
            <View style={styles.earningsGrid}>
              <View style={styles.earningsCard}>
                <Text style={styles.earningsLabel}>Available to Claim</Text>
                <Text style={styles.earningsValue}>{formatHostReward(pendingCents / 100)}</Text>
                {canClaim ? (
                  <TouchableOpacity
                    style={styles.claimButton}
                    onPress={handleClaimEarnings}
                    disabled={claiming}
                  >
                    {claiming ? (
                      <ActivityIndicator size="small" color="#000" />
                    ) : (
                      <Text style={styles.claimButtonText}>Claim Now</Text>
                    )}
                  </TouchableOpacity>
                ) : (
                  <Text style={styles.earningsSubtext}>
                    Min: $10.00
                  </Text>
                )}
              </View>

              <View style={styles.earningsCard}>
                <Text style={styles.earningsLabel}>Total Received</Text>
                <Text style={styles.earningsValue}>{formatHostReward(totalEarnedCents / 100)}</Text>
                <Text style={styles.earningsSubtext}>All-time rewards</Text>
              </View>

              <View style={styles.earningsCard}>
                <Text style={styles.earningsLabel}>Already Claimed</Text>
                <Text style={styles.earningsValue}>{formatHostReward(claimedCents / 100)}</Text>
                <Text style={styles.earningsSubtext}>Released to wallet</Text>
              </View>
            </View>
          ) : (
            <View style={styles.earningsGrid}>
              <View style={styles.earningsCard}>
                <Text style={styles.earningsLabel}>Base Reward</Text>
                <Text style={styles.earningsValue}>{HOST_POT_PCT_LABEL}</Text>
                <Text style={styles.earningsSubtext}>of each round's Crit pool, paid in Crits</Text>
              </View>
              <View style={styles.earningsCard}>
                <Text style={styles.earningsLabel}>Stay-On Bonus</Text>
                <Text style={styles.earningsValue}>up to 2x</Text>
                <Text style={styles.earningsSubtext}>each extra round you host in an event pays more</Text>
              </View>
              <View style={styles.earningsCard}>
                <Text style={styles.earningsLabel}>Crits Earned</Text>
                <Text style={styles.earningsValue}>{fmtCrits(stats.totalCritsEarned || 0)}</Text>
                <Text style={styles.earningsSubtext}>All-time hosting</Text>
              </View>
            </View>
          )}
        </View>
        ) : null}

        {/* Statistics Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your Statistics</Text>
          
          <View style={styles.statsGrid}>
            <StatBox label="Matches Hosted" value={stats.totalMatchesHosted || 0} />
            <StatBox label="Success Rate" value={`${successRate}%`} />
            <StatBox label="Events Completed" value={stats.eventsCompletedAsHost || 0} />
            <StatBox label="Avg Rating" value={totalRatings > 0 ? `${displayRating}/5` : 'No ratings'} accent={totalRatings > 0} />
          </View>
        </View>

        {/* Rating Breakdown (Phase 5) — only show if user has ratings */}
        {totalRatings > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Your Ratings ({totalRatings} total)</Text>
            <View style={styles.infoCard}>
              {Object.entries(ratingBreakdown).map(([category, data]) => {
                const avg = data?.average || 0;
                const stars = Math.round(avg / 2 * 10) / 10; // 1-10 to 0.5-5 scale
                const fillWidth = `${(avg / 10) * 100}%`;
                return (
                  <View key={category} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}>
                    <Text style={{ color: '#aaa', fontSize: 12, flex: 1, textTransform: 'capitalize' }}>
                      {category.replace(/_/g, ' ')}
                    </Text>
                    <View style={{ width: 80, height: 6, backgroundColor: '#333', borderRadius: 3, marginRight: 8 }}>
                      <View style={{ width: fillWidth, height: '100%', backgroundColor: '#FCD000', borderRadius: 3 }} />
                    </View>
                    <Text style={{ color: '#FCD000', fontSize: 12, fontWeight: '700', width: 30, textAlign: 'right' }}>
                      {stars.toFixed(1)}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* How It Works Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>How It Works</Text>
          <Text style={[styles.optInDescription, { marginBottom: 14 }]}>
            {critsEnabled
              ? 'You earn only on events you sign up to host. Pick the events you\'ll host — each one rewards you. Hosts are never assigned automatically; only sign-ups are rewarded.'
              : 'You host only the events you sign up for. Pick the events you\'ll host — every round you run goes on your record. Hosts are never assigned automatically.'}
          </Text>

          <View style={styles.infoCard}>
            <InfoRow
              icon="checkmark"
              title="1. Sign up to host a round"
              description="Turn on hosting, then claim a host spot on an event that needs one. It's free — you don't pay an entry to host."
            />
            {critsEnabled ? (
              <>
                <InfoRow
                  icon="star"
                  title={`2. Earn ${HOST_POT_PCT_LABEL} of that round's Crit pool`}
                  description={`A round's Crit pool is the players in that match times the entry cost. You keep ${HOST_POT_PCT_LABEL} of it, paid in Crits. A packed 15-racer lobby pays a lot more than a 1v1 — and every round pays at least 1 Crit.`}
                />
                <InfoRow
                  icon="trending-up"
                  title="3. Keep hosting for a bigger multiplier"
                  description="Your first round in an event pays 1x. Host the next round too and it pays 1.25x, then 1.5x, and so on — up to 2x once you're hosting 5+ rounds of the same event. The more of an event you carry, the more each round is worth."
                />
                <InfoRow
                  icon="trophy"
                  title="4. Spend your Crits to play"
                  description="Crits aren't cash. Use the ones you earn hosting to enter events as a player — hosting keeps you in the action without paying entry."
                />
              </>
            ) : (
              <>
                <InfoRow
                  icon="people"
                  title="2. Run your rounds"
                  description="Get the lobby set, start on time, and keep it moving. Every round you host goes on your record."
                />
                <InfoRow
                  icon="trending-up"
                  title="3. Build your record"
                  description="Hosted rounds build your hosting record. The more of an event you carry, the more it shows."
                />
              </>
            )}
          </View>

          {/* Worked example — numbers computed live from the reward config. */}
          {critsEnabled ? (
          <View style={styles.exampleCard}>
            <Text style={styles.exampleTitle}>A real example</Text>
            <Text style={styles.exampleIntro}>
              You host a GT7 race with {EX_RACERS} racers, {EX_ENTRY} Crits to enter.
              That round's Crit pool is {EX_RACERS} × {EX_ENTRY} = {EX_POT} Crits, so your
              {' '}{HOST_POT_PCT_LABEL} base share is {EX_ROUNDS[0].crits} Crits. Stay on to host
              more rounds and the multiplier climbs:
            </Text>
            {EX_ROUNDS.map((r, i) => (
              <View key={i} style={styles.exampleRow}>
                <Text style={styles.exampleRowLabel}>Round {i + 1}</Text>
                <Text style={styles.exampleRowMult}>{r.depthMultiplier}x</Text>
                <Text style={styles.exampleRowValue}>{fmtCrits(r.crits)}</Text>
              </View>
            ))}
            <View style={styles.exampleTotalRow}>
              <Text style={styles.exampleTotalLabel}>Just those 3 rounds</Text>
              <Text style={styles.exampleTotalValue}>{fmtCrits(EX_TOTAL)}</Text>
            </View>
            <Text style={styles.exampleFootnote}>
              Bigger lobbies and more rounds = more Crits. Even a small lobby never pays less than 1 Crit for a round.
            </Text>
          </View>
          ) : null}
        </View>

        {/* Failure Penalties Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Failure Penalties</Text>
          <Text style={styles.sectionSubtitle}>
            Hosting is a responsibility. Failing to fulfill your duties has consequences.
          </Text>
          
          <View style={styles.penaltyCard}>
            <View style={styles.penaltyHeader}>
              <Ionicons name="warning" size={20} color="#f39c12" />
              <Text style={styles.penaltyHeaderText}>30-Day Rolling Window</Text>
            </View>
            <Text style={styles.penaltyDescription}>
              Failures are tracked within a 30-day window. Stay reliable for 30 days and your recent failure count resets.
            </Text>
          </View>

          <View style={styles.penaltyList}>
            <View style={styles.penaltyRow}>
              <View style={[styles.penaltyBadge, { backgroundColor: '#2ecc7133', borderColor: '#2ecc71' }]}>
                <Text style={[styles.penaltyBadgeText, { color: '#2ecc71' }]}>1st</Text>
              </View>
              <View style={styles.penaltyContent}>
                <Text style={styles.penaltyTitle}>Warning</Text>
                <Text style={styles.penaltyText}>No penalty. Everyone gets one chance.</Text>
              </View>
            </View>

            <View style={styles.penaltyRow}>
              <View style={[styles.penaltyBadge, { backgroundColor: '#f39c1233', borderColor: '#f39c12' }]}>
                <Text style={[styles.penaltyBadgeText, { color: '#f39c12' }]}>2nd</Text>
              </View>
              <View style={styles.penaltyContent}>
                <Text style={styles.penaltyTitle}>Strike</Text>
                <Text style={styles.penaltyText}>Logged against your hosting record. One more triggers a final warning.</Text>
              </View>
            </View>

            <View style={styles.penaltyRow}>
              <View style={[styles.penaltyBadge, { backgroundColor: '#e74c3c33', borderColor: '#e74c3c' }]}>
                <Text style={[styles.penaltyBadgeText, { color: '#e74c3c' }]}>3rd</Text>
              </View>
              <View style={styles.penaltyContent}>
                <Text style={styles.penaltyTitle}>Final Warning + Hosting Suspended</Text>
                <Text style={styles.penaltyText}>This is your last chance. Next failure = ban.</Text>
              </View>
            </View>

            <View style={styles.penaltyRow}>
              <View style={[styles.penaltyBadge, { backgroundColor: '#c0392b33', borderColor: '#c0392b' }]}>
                <Text style={[styles.penaltyBadgeText, { color: '#c0392b' }]}>4th</Text>
              </View>
              <View style={styles.penaltyContent}>
                <Text style={styles.penaltyTitle}>Account Ban</Text>
                <Text style={styles.penaltyText}>Your account will be permanently banned.</Text>
              </View>
            </View>
          </View>

          <View style={styles.penaltyWarning}>
            <Ionicons name="alert-circle" size={18} color="#e74c3c" />
            <Text style={styles.penaltyWarningText}>
              8+ lifetime failures (even spread across years) also results in a permanent ban. Take hosting seriously.
            </Text>
          </View>
        </View>

        {/* Tier System Section — dormant under the Crit pot-share model */}
        {HOST_CASH_TIERS && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Host Tiers</Text>
            <Text style={styles.sectionSubtitle}>
              Advance through 10 tiers by hosting matches. Higher tiers earn a bigger share of the host ceiling!
            </Text>

            <View style={styles.tierList}>
              {HOST_REWARD_CONFIG.tierThresholds.map((t) => (
                <TierRow
                  key={t.tier}
                  tier={t.tier}
                  name={t.name}
                  points={t.points}
                  detail={`${(t.multiplier * 100).toFixed(0)}% multiplier`}
                  color={t.color}
                  current={tier.tier === t.tier}
                />
              ))}
            </View>
          </View>
        )}

        {/* Disclaimer */}
        <View style={styles.disclaimerSection}>
          <Text style={styles.disclaimerTitle}>{critsEnabled ? 'About Host Rewards' : 'About Hosting'}</Text>
          {critsEnabled ? (
          <Text style={styles.disclaimerText}>
            Hosting earns you {HOST_POT_PCT_LABEL} of each lobby's Crit pool per round,
            minted as Crits — a non-cash reward you can spend to enter more events.
            Hosting more rounds of an event raises the multiplier on each one.
            Rewards are platform-issued Crits, never taken from the players' Crit
            pool, and go only to hosts who sign up to host; the role is never
            auto-assigned. Crits are not redeemable for cash.
          </Text>
          ) : (
          <Text style={styles.disclaimerText}>
            Hosting is free and always your choice. Hosts who sign up run the
            rounds they claimed; the role is never auto-assigned. Hosted rounds
            go on your record.
          </Text>
          )}
        </View>

        <View style={{ height: 100 }} />
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// Helper Components
const StatBox = ({ label, value, accent }) => (
  <View style={[styles.statBox, accent && styles.statBoxAccent]}>
    <Text style={styles.statValue}>{value}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </View>
);

const InfoRow = ({ icon, title, description }) => (
  <View style={styles.infoRow}>
    <View style={styles.infoIconContainer}>
      {getIcon(icon, 24, '#FCD000')}
    </View>
    <View style={styles.infoContent}>
      <Text style={styles.infoTitle}>{title}</Text>
      <Text style={styles.infoDescription}>{description}</Text>
    </View>
  </View>
);

const TierRow = ({ tier, name, points, detail, color, current }) => (
  <View style={[styles.tierRow, current && styles.tierRowActive]}>
    <View style={[styles.tierRowBadge, { backgroundColor: color + '20', borderColor: color }]}>
      <FontAwesome5 name="star" size={16} color={color} style={styles.tierRowIcon} />
      <Text style={[styles.tierNumber, { color }]}>{tier}</Text>
    </View>
    <View style={styles.tierRowContent}>
      <Text style={[styles.tierRowName, current && { color: '#FCD000' }]}>{name}</Text>
      <Text style={styles.tierRowRequirement}>
        {points === 0 ? 'Starting tier' : `${points}+ Host Points`} - {detail}
      </Text>
    </View>
    {current && <Text style={styles.currentBadge}>CURRENT</Text>}
  </View>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0A0A',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#FFF',
    marginTop: 10,
    fontSize: 16,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    color: '#FF4444',
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#FCD000',
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: {
    color: '#000',
    fontWeight: 'bold',
    fontSize: 16,
  },
  scrollView: {
    flex: 1,
  },
  heroSection: {
    alignItems: 'center',
    paddingVertical: 30,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  tierBadgeLarge: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 5,
  },
  heroSubtitle: {
    fontSize: 16,
    color: '#999',
    marginBottom: 15,
  },
  tierBadge: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#1A1A1A',
  },
  tierBadgeText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  hostPointsText: {
    fontSize: 18,
    color: '#FCD000',
    fontWeight: 'bold',
    marginTop: 10,
  },
  progressText: {
    fontSize: 14,
    color: '#999',
    marginTop: 5,
  },
  section: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 5,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#999',
    marginBottom: 15,
  },
  optInCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A1A1A',
    padding: 20,
    borderRadius: 12,
  },
  optInLeft: {
    flex: 1,
    marginRight: 15,
  },
  optInTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 5,
  },
  optInDescription: {
    fontSize: 14,
    color: '#999',
    lineHeight: 20,
  },
  earningsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -5,
  },
  earningsCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#1A1A1A',
    padding: 15,
    margin: 5,
    borderRadius: 12,
    alignItems: 'center',
  },
  earningsLabel: {
    fontSize: 12,
    color: '#999',
    marginBottom: 5,
    textAlign: 'center',
  },
  earningsValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 10,
  },
  earningsSubtext: {
    fontSize: 11,
    color: '#666',
    textAlign: 'center',
  },
  claimButton: {
    backgroundColor: '#FCD000',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 6,
    marginTop: 5,
  },
  claimButtonText: {
    color: '#000',
    fontWeight: 'bold',
    fontSize: 14,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -5,
  },
  statBox: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#1A1A1A',
    padding: 15,
    margin: 5,
    borderRadius: 12,
    alignItems: 'center',
  },
  statBoxAccent: {
    borderWidth: 2,
    borderColor: '#FCD000',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 5,
  },
  statLabel: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
  },
  infoCard: {
    backgroundColor: '#1A1A1A',
    borderRadius: 12,
    padding: 15,
  },
  // Worked-example card (How It Works)
  exampleCard: {
    backgroundColor: '#12140A',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FCD00040',
    padding: 16,
    marginTop: 14,
  },
  exampleTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#FCD000',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  exampleIntro: {
    fontSize: 14,
    color: '#CCC',
    lineHeight: 21,
    marginBottom: 12,
  },
  exampleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A16',
  },
  exampleRowLabel: {
    flex: 1,
    fontSize: 14,
    color: '#FFF',
    fontWeight: '600',
  },
  exampleRowMult: {
    width: 70,
    textAlign: 'center',
    fontSize: 13,
    color: '#9A9A7A',
  },
  exampleRowValue: {
    width: 90,
    textAlign: 'right',
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FCD000',
  },
  exampleTotalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    marginTop: 4,
  },
  exampleTotalLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FFF',
  },
  exampleTotalValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FCD000',
  },
  exampleFootnote: {
    fontSize: 12,
    color: '#8A8A6A',
    lineHeight: 18,
    marginTop: 10,
  },
  infoRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A2A',
  },
  infoIconContainer: {
    width: 40,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 2,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 5,
  },
  infoDescription: {
    fontSize: 14,
    color: '#999',
    lineHeight: 20,
  },
  tierList: {
    marginTop: 10,
  },
  tierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A1A1A',
    padding: 15,
    marginBottom: 10,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  tierRowActive: {
    borderColor: '#FCD000',
    backgroundColor: '#2A2A0A',
  },
  tierRowBadge: {
    width: 50,
    height: 50,
    borderRadius: 25,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  tierRowIcon: {
    position: 'absolute',
    top: 8,
  },
  tierNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    position: 'absolute',
    bottom: 5,
  },
  tierRowContent: {
    flex: 1,
  },
  tierRowName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 3,
  },
  tierRowRequirement: {
    fontSize: 12,
    color: '#999',
  },
  currentBadge: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#FCD000',
    backgroundColor: '#2A2A0A',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  // Disclaimer Section
  disclaimerSection: {
    padding: 20,
    backgroundColor: '#111',
  },
  disclaimerTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#666',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  disclaimerText: {
    fontSize: 11,
    color: '#666',
    lineHeight: 18,
  },
  // Penalty Section Styles
  penaltyCard: {
    backgroundColor: '#1A1A1A',
    borderRadius: 12,
    padding: 15,
    marginBottom: 15,
    borderLeftWidth: 3,
    borderLeftColor: '#f39c12',
  },
  penaltyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  penaltyHeaderText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#f39c12',
  },
  penaltyDescription: {
    fontSize: 14,
    color: '#999',
    lineHeight: 20,
  },
  penaltyList: {
    backgroundColor: '#1A1A1A',
    borderRadius: 12,
    padding: 10,
    marginBottom: 15,
  },
  penaltyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A2A',
  },
  penaltyBadge: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  penaltyBadgeText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  penaltyContent: {
    flex: 1,
  },
  penaltyTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FFF',
    marginBottom: 2,
  },
  penaltyText: {
    fontSize: 13,
    color: '#999',
  },
  penaltyWarning: {
    flexDirection: 'row',
    backgroundColor: '#2a1a1a',
    padding: 12,
    borderRadius: 8,
    gap: 10,
    borderWidth: 1,
    borderColor: '#e74c3c33',
  },
  penaltyWarningText: {
    flex: 1,
    fontSize: 13,
    color: '#e74c3c',
    lineHeight: 20,
  },
  // Modal Styles
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  modalScroll: {
    maxHeight: '100%',
  },
  modalScrollContent: {
    padding: 20,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  modalIconContainer: {
    marginBottom: 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFF',
    textAlign: 'center',
  },
  modalContent: {
    marginBottom: 20,
  },
  modalSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FCD000',
    marginTop: 15,
    marginBottom: 10,
  },
  modalText: {
    fontSize: 14,
    color: '#CCC',
    lineHeight: 22,
  },
  modalBold: {
    fontWeight: 'bold',
    color: '#FFF',
  },
  modalListItem: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  modalBullet: {
    color: '#FCD000',
    fontSize: 16,
    marginRight: 10,
  },
  modalListText: {
    flex: 1,
    fontSize: 14,
    color: '#CCC',
    lineHeight: 20,
  },
  modalNote: {
    flexDirection: 'row',
    backgroundColor: '#1a2a3a',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    gap: 10,
  },
  modalNoteText: {
    flex: 1,
    fontSize: 13,
    color: '#AAA',
    lineHeight: 20,
  },
  modalDisclaimer: {
    backgroundColor: '#2A2A0A',
    padding: 15,
    borderRadius: 8,
    marginTop: 20,
  },
  modalDisclaimerText: {
    fontSize: 13,
    color: '#999',
    lineHeight: 20,
    textAlign: 'center',
  },
  modalLink: {
    color: '#FCD000',
    textDecorationLine: 'underline',
    marginTop: 8,
  },
  modalActions: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 15,
    alignItems: 'center',
  },
  modalButtonDecline: {
    backgroundColor: '#2A2A2A',
  },
  modalButtonAccept: {
    backgroundColor: '#FCD000',
  },
  modalButtonTextDecline: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalButtonTextAccept: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  // Modal Penalty Styles
  modalPenaltyList: {
    marginTop: 10,
    marginBottom: 10,
  },
  modalPenaltyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 12,
  },
  modalPenaltyBadge: {
    fontSize: 14,
    fontWeight: 'bold',
    width: 32,
  },
  modalPenaltyText: {
    flex: 1,
    fontSize: 14,
    color: '#CCC',
  },
  modalWarningBox: {
    flexDirection: 'row',
    backgroundColor: '#2a1a1a',
    padding: 12,
    borderRadius: 8,
    marginTop: 10,
    marginBottom: 10,
    gap: 10,
    borderWidth: 1,
    borderColor: '#e74c3c33',
  },
  modalWarningText: {
    flex: 1,
    fontSize: 12,
    color: '#e74c3c',
    lineHeight: 18,
  },
});